import { useCallback, useEffect, useState } from 'react'
import {
  Check,
  ChevronRight,
  File,
  FileArchive,
  FileCode2,
  FileImage,
  FileSpreadsheet,
  FileText,
  Folder,
  Globe,
  Link2,
  Lock,
  Upload,
} from 'lucide-react'
import { cn } from 'tailwind-v4/lib/utils'
import config from '@/config'
import { getToken } from '@/utils/AuthTokenHelper'
import { PanelFrame } from './panel-frame'
import {
  breadcrumb,
  fileKind,
  finderOrder,
  kindLabel,
  sizeLabel,
  uploadPath,
  type FileKind,
} from './workspace-path'

/** One glyph per kind, the way Finder does it. */
const KIND_ICON: Record<FileKind, typeof File> = {
  folder: Folder,
  image: FileImage,
  code: FileCode2,
  data: FileSpreadsheet,
  document: FileText,
  text: FileText,
  archive: FileArchive,
  other: File,
}

/**
 * `workspace`: the organisation's own files, as folders.
 *
 * THE SAME FILES THE AGENT SEES, read through the workspace routes that
 * already exist against the same bucket its tools browse. A file browser
 * showing something different from what the agent can read would be worse
 * than none, because you would ask it about a file it cannot see.
 *
 * DROP ONTO A FOLDER TO PUT IT THERE. The row is the target, which is the
 * only arrangement that needs no explaining. Dropping on the panel itself
 * lands in the folder being looked at.
 *
 * THE DROP MUST STOP PROPAGATING, and that is not a nicety. The agent
 * surface listens for drops on the WINDOW, because the layer is
 * `pointer-events: none` and would otherwise lose them to the page. So a
 * drop in here would ALSO be read as "attach this to the conversation"
 * unless it is stopped. Two different intentions, one gesture, and the
 * nearer target wins.
 *
 * SHARING IS A TIME-LIMITED LINK, not a public file. See `share` below.
 */

interface Entry {
  name: string
  path: string
  type: 'file' | 'directory' | string
  size?: number
}

const api = (orgId: string) =>
  `${config.url.trunk}/organisations/${orgId}/workspace`

const auth = () => ({ Authorization: `Bearer ${getToken()?.accessToken ?? ''}` })

export function WorkspacePanel({
  onClose,
  orgId,
  onOpenDiagram,
}: {
  onClose: () => void
  orgId?: string
  /**
   * Open a `.excalidraw` on the canvas.
   *
   * A diagram saved out of a conversation should be openable from the
   * folder it landed in, or saving it was only a way of losing it
   * somewhere tidy.
   */
  onOpenDiagram?: (title: string, elements: unknown[]) => void
}) {
  const [path, setPath] = useState('/')
  const [items, setItems] = useState<Entry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  /** The folder a drag is currently over, so the row can show it. */
  const [over, setOver] = useState<string | null>(null)
  /** The file whose link was just copied, for the tick. */
  const [shared, setShared] = useState<string | null>(null)
  /**
   * Names in this folder that are public.
   *
   * One call for the folder rather than a check per row: forty files
   * would otherwise be forty requests to render a list. Derived from the
   * bucket, so there is no record to drift out of step with what is
   * actually reachable.
   */
  const [publicUrls, setPublicUrls] = useState<Map<string, string>>(new Map())
  const [toggling, setToggling] = useState<string | null>(null)

  const load = useCallback(
    async (at: string) => {
      if (!orgId) return
      setItems(null)
      setError(null)
      try {
        const res = await fetch(
          `${api(orgId)}/files?path=${encodeURIComponent(at)}`,
          { headers: auth() },
        )
        if (!res.ok) throw new Error(String(res.status))
        setItems(((await res.json()) as { items: Entry[] }).items)

        // Which of them are public. A failure here loses the badges, not
        // the listing: knowing the files is more use than knowing their
        // visibility, so it does not take the folder down with it.
        try {
          const shown = await fetch(
            `${api(orgId)}/public?path=${encodeURIComponent(at)}`,
            { headers: auth() },
          )
          if (shown.ok) {
            const body = (await shown.json()) as {
              files: { name: string; url: string }[]
            }
            setPublicUrls(new Map(body.files.map((f) => [f.name, f.url])))
          }
        } catch {
          setPublicUrls(new Map())
        }
      } catch {
        setError('Could not read that folder.')
      }
    },
    [orgId],
  )

  useEffect(() => {
    void load(path)
  }, [load, path])

  const upload = useCallback(
    async (files: File[], into: string) => {
      if (!orgId || files.length === 0) return
      setBusy(into)
      setError(null)
      try {
        for (const file of files) {
          const presigned = await fetch(`${api(orgId)}/presign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...auth() },
            body: JSON.stringify({
              path: uploadPath(into, file.name),
              mimeType: file.type || 'application/octet-stream',
              size: file.size,
            }),
          })
          if (!presigned.ok) throw new Error('presign')
          const { uploadUrl } = (await presigned.json()) as { uploadUrl: string }
          const put = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            body: file,
          })
          if (!put.ok) throw new Error('put')
        }
        // Only reload if we dropped into the folder on screen; a drop onto
        // a subfolder has not changed what is listed.
        if (into === path) await load(path)
      } catch {
        setError('That would not upload.')
      } finally {
        setBusy(null)
      }
    },
    [orgId, path, load],
  )

  /**
   * Public, or private again.
   *
   * PUBLISHING COPIES, it never moves: the private original stays where
   * it is, so taking it back cannot lose the file. `public/files/` is the
   * only part of this bucket the internet can read, and it reaches it
   * through a CloudFront behaviour that exists for exactly this.
   *
   * PRIVATE IS THE DEFAULT and stays the default. A venue's revenue
   * export should take a deliberate press to put on the open internet,
   * and the row says plainly which it is rather than leaving anybody to
   * guess.
   */
  const setVisibility = useCallback(
    async (file: Entry, makePublic: boolean) => {
      if (!orgId) return
      setToggling(file.path)
      setError(null)
      try {
        const res = await fetch(`${api(orgId)}/visibility`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...auth() },
          body: JSON.stringify({ path: file.path, public: makePublic }),
        })
        if (!res.ok) throw new Error(String(res.status))
        const { url } = makePublic
          ? ((await res.json()) as { url?: string })
          : { url: undefined }
        setPublicUrls((now) => {
          const next = new Map(now)
          if (makePublic && url) next.set(file.name, url)
          else next.delete(file.name)
          return next
        })
        // Copied on the way out, because the first thing anybody wants
        // after making something public is the link to it.
        if (url) {
          await navigator.clipboard.writeText(url)
          setShared(file.path)
          window.setTimeout(() => {
            setShared((now) => (now === file.path ? null : now))
          }, 2500)
        }
      } catch {
        setError(
          makePublic ? 'Could not make that public.' : 'Could not make that private.',
        )
      } finally {
        setToggling(null)
      }
    },
    [orgId],
  )

  /**
   * A link to a file, whichever it is.
   *
   * Public gets its permanent url; private gets a presigned one that dies
   * in an hour. Both are "copy a link", and the row already says which
   * kind of file it is, so the button does not need to.
   */
  const copyLink = useCallback(
    async (file: Entry) => {
      if (!orgId) return
      try {
        // A public file already has its address, handed over with the
        // listing. A private one gets a presigned link that dies in an
        // hour.
        const known = publicUrls.get(file.name)
        const url = known
          ? known
          : await (async () => {
              const res = await fetch(
                `${api(orgId)}/presign-download?path=${encodeURIComponent(file.path)}`,
                { headers: auth() },
              )
              if (!res.ok) throw new Error(String(res.status))
              return ((await res.json()) as { downloadUrl: string }).downloadUrl
            })()
        await navigator.clipboard.writeText(url)
        setShared(file.path)
        window.setTimeout(() => {
          setShared((now) => (now === file.path ? null : now))
        }, 2500)
      } catch {
        setError('Could not make a link for that.')
      }
    },
    [orgId, publicUrls],
  )

  /** Drop handlers for a target folder. See the note on propagation. */
  const dropInto = (folder: string) => ({
    onDragOver: (event: React.DragEvent) => {
      if (![...event.dataTransfer.types].includes('Files')) return
      event.preventDefault()
      event.stopPropagation()
      setOver(folder)
    },
    onDragLeave: () => {
      setOver((now) => (now === folder ? null : now))
    },
    onDrop: (event: React.DragEvent) => {
      if (![...event.dataTransfer.types].includes('Files')) return
      event.preventDefault()
      // The window also listens, and would attach these to the
      // conversation. The nearer target wins.
      event.stopPropagation()
      setOver(null)
      void upload([...event.dataTransfer.files], folder)
    },
  })

  /**
   * Read a diagram back off the workspace.
   *
   * Through a presigned GET rather than the raw file route, because the
   * bucket is private and this is the one path that already hands out a
   * short-lived read for exactly this.
   */
  const openDiagram = useCallback(
    async (file: Entry) => {
      if (!orgId || !onOpenDiagram) return
      setError(null)
      try {
        const res = await fetch(
          `${api(orgId)}/presign-download?path=${encodeURIComponent(file.path)}`,
          { headers: auth() },
        )
        if (!res.ok) throw new Error(String(res.status))
        const { downloadUrl } = (await res.json()) as { downloadUrl: string }
        const body = await fetch(downloadUrl)
        if (!body.ok) throw new Error(String(body.status))
        const scene = (await body.json()) as { elements?: unknown[] }
        if (!Array.isArray(scene.elements)) throw new Error('not a scene')
        onOpenDiagram(file.name.replace(/\.excalidraw$/, ''), scene.elements)
      } catch {
        setError('That did not open as a diagram.')
      }
    },
    [orgId, onOpenDiagram],
  )

  const rows = finderOrder(items ?? [])

  return (
    <PanelFrame id="workspace" onClose={onClose}>
      <div
        className={cn(
          'flex min-h-0 min-w-0 flex-col rounded-lg',
          over === path && 'ring-2 ring-[var(--brand-hot,#ff2d87)]',
        )}
        {...dropInto(path)}
      >
        {/* The trail, every level clickable. Finder's path bar, which is
            how anybody gets back three folders in one go rather than
            pressing Up three times. */}
        <nav className="flex min-w-0 flex-wrap items-center gap-0.5 pb-2">
          {breadcrumb(path).map((crumb, i, all) => (
            <span key={crumb.path} className="flex min-w-0 items-center gap-0.5">
              {i > 0 ? (
                <ChevronRight className="size-3 shrink-0 text-muted-foreground/50" />
              ) : null}
              <button
                type="button"
                disabled={i === all.length - 1}
                onClick={() => {
                  setPath(crumb.path)
                }}
                className={cn(
                  'max-w-[10rem] truncate rounded px-1 py-0.5 text-xs',
                  i === all.length - 1
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {crumb.name}
              </button>
            </span>
          ))}
          <label className="ml-auto flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-black/10 px-2 py-1 text-xs text-muted-foreground hover:text-foreground dark:border-white/10">
            <Upload className="size-3.5" />
            {busy === path ? 'Uploading…' : 'Upload'}
            <input
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                void upload([...(event.target.files ?? [])], path)
                event.target.value = ''
              }}
            />
          </label>
        </nav>

        {/* Column headings. Finder's, minus the date: the listing route
            returns name, type and size only, so Kind takes that column. */}
        <div className="flex shrink-0 items-center gap-2 border-b border-black/5 px-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground/60 dark:border-white/5">
          <span className="w-4 shrink-0" />
          <span className="min-w-0 flex-1">Name</span>
          <span className="w-20 shrink-0 text-right">Size</span>
          <span className="hidden w-20 shrink-0 sm:block">Kind</span>
          <span className="w-14 shrink-0 text-right">Shared</span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {error ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">{error}</p>
          ) : null}
          {!error && items === null ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">Reading…</p>
          ) : null}
          {items?.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              Nothing in here. Drop files on this panel, or on a folder to put
              them inside it.
            </p>
          ) : null}

          <ul className="flex min-w-0 flex-col">
            {rows.map((entry) => {
              const isFolder = entry.type === 'directory'
              const kind = fileKind(entry.name, isFolder)
              const Icon = KIND_ICON[kind]
              const isPublic = !isFolder && publicUrls.has(entry.name)
              const isDiagram =
                !isFolder && entry.name.toLowerCase().endsWith('.excalidraw')
              return (
                <li
                  key={entry.path}
                  title={
                    isFolder
                      ? undefined
                      : isPublic
                        ? `${entry.name} — public file, anyone with the address can read it`
                        : `${entry.name} — private file, not public, no web address`
                  }
                  className={cn(
                    'group flex min-w-0 items-center gap-2 rounded-md px-2',
                    'hover:bg-black/5 dark:hover:bg-white/5',
                    isFolder &&
                      over === entry.path &&
                      'ring-2 ring-[var(--brand-hot,#ff2d87)]',
                  )}
                  {...(isFolder ? dropInto(entry.path) : {})}
                >
                  <button
                    type="button"
                    disabled={!isFolder && !isDiagram}
                    onClick={() => {
                      if (isFolder) setPath(entry.path)
                      else if (isDiagram) void openDiagram(entry)
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left disabled:cursor-default"
                  >
                    <Icon
                      className={cn(
                        'size-4 shrink-0',
                        isFolder ? 'text-[var(--brand-glow,#ffa234)]' : 'text-muted-foreground',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                      {entry.name}
                    </span>
                    <span className="w-20 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                      {isFolder
                        ? busy === entry.path
                          ? 'uploading…'
                          : '—'
                        : sizeLabel(entry.size)}
                    </span>
                    <span className="hidden w-20 shrink-0 text-xs text-muted-foreground sm:block">
                      {kindLabel(kind)}
                    </span>
                  </button>

                  {/* Visibility and a link, on the row. The columns are
                      always reserved so nothing jumps as you move down,
                      and the lock or globe is ALWAYS visible for a file
                      rather than appearing on hover: which files are
                      public is the thing somebody scans a folder for. */}
                  <span className="flex w-14 shrink-0 items-center justify-end gap-0.5">
                    {!isFolder ? (
                      <>
                        <button
                          type="button"
                          aria-label={
                            isPublic
                              ? `${entry.name} is public. Make it private`
                              : `${entry.name} is private. Make it public`
                          }
                          aria-pressed={isPublic}
                          /**
                           * SAYS WHAT IT IS, then what pressing does.
                           * These were written as instructions, so
                           * hovering a file told you what would happen
                           * rather than what was already true, and the
                           * state is the thing somebody is checking.
                           */
                          title={
                            isPublic
                              ? 'Public file. It has a web address and anyone with it can read the file.\nPress to make it private again.'
                              : 'Private file. Not public, and it has no web address.\nPress to make it public.'
                          }
                          disabled={toggling === entry.path}
                          onClick={() => {
                            void setVisibility(entry, !isPublic)
                          }}
                          className={cn(
                            'rounded p-1',
                            isPublic
                              ? 'text-[var(--brand-glow,#ffa234)]'
                              : 'text-muted-foreground/50 hover:text-foreground',
                            toggling === entry.path && 'animate-pulse',
                          )}
                        >
                          {isPublic ? (
                            <Globe className="size-3.5" />
                          ) : (
                            <Lock className="size-3.5" />
                          )}
                        </button>
                        <button
                          type="button"
                          aria-label={
                            isPublic
                              ? `Copy the public address of ${entry.name}`
                              : `Copy a temporary link to ${entry.name}`
                          }
                          title={
                            isPublic
                              ? 'Copy its public web address. It does not expire while the file is public.'
                              : 'This file is private, so it has no web address.\nCopies a temporary link instead, which stops working after an hour.'
                          }
                          onClick={() => {
                            void copyLink(entry)
                          }}
                          className={cn(
                            'rounded p-1 text-muted-foreground hover:text-foreground',
                            shared === entry.path
                              ? 'opacity-100'
                              : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                          )}
                        >
                          {shared === entry.path ? (
                            <Check className="size-3.5 text-[var(--live,#3f7d55)]" />
                          ) : (
                            <Link2 className="size-3.5" />
                          )}
                        </button>
                      </>
                    ) : null}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>

        {/* Finder's status bar: how many things, and that none of them are
            public. The second half matters more than the first. */}
        <div className="shrink-0 border-t border-black/5 px-2 pt-1.5 text-[11px] text-muted-foreground/70 dark:border-white/5">
          {items === null
            ? ' '
            : publicUrls.size === 0
              ? `${String(items.length)} item${items.length === 1 ? '' : 's'} · all private`
              : `${String(items.length)} item${items.length === 1 ? '' : 's'} · ${String(publicUrls.size)} public, anyone with the link`}
        </div>
      </div>
    </PanelFrame>
  )
}
